import React, { useState, useCallback } from 'react';
import { debounce, flatten } from 'lodash-es';
import {
  Autocomplete,
  TextField as MuiTextField,
  Chip,
  InputBaseComponentProps,
  Select,
  MenuItem,
  InputLabel,
  FormControl as MuiFormControl
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useLocales, useTheme } from '../../../hooks';
import { SuggestionsAutocomplete } from '../../shared/SuggestionsAutocomplete';
import {
  SmartQueryFieldSymbol,
  SmartQueryOperatorSymbol,
  SmartQueryEnumItem,
  SmartQueryFieldResponse
} from '../../../API';
import { BETWEEN_OPERATOR_SYMBOL } from '../../../utils/queryBuilder';
import { DateTime } from 'luxon';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

const useStyles = makeStyles()(() => ({
  textField: {
    '&.MuiTextField-root': {
      marginBottom: '0 !important'
    }
  },
  fieldContainer: {
    minWidth: 200
  }
}));

interface SmartQueryFieldRendererProps {
  field: SmartQueryFieldResponse;
  value: string | number | string[] | number[] | boolean | undefined;
  onChange: (value: string | number | string[] | number[] | boolean | undefined) => void;
  operator?: SmartQueryOperatorSymbol;
  onOperatorChange?: (operator: SmartQueryOperatorSymbol) => void;
  disabled?: boolean;
  className?: string;
  variant?: 'standard' | 'outlined' | 'filled';
}

/**
 * Shared component for rendering smart query field inputs based on field type
 * Used by both CollectionQueryNodeForm and ContentSearchSmartFilters
 */
function SmartQueryFieldRenderer({
  field,
  value,
  onChange,
  operator = SmartQueryOperatorSymbol.CONTAINS,
  onOperatorChange,
  disabled = false,
  className,
  variant = 'standard'
}: SmartQueryFieldRendererProps): JSX.Element {
  const { classes, cx } = useStyles();
  const { localize, t } = useLocales();
  const { formControlColor } = useTheme();
  const [textFieldValueBuffer, setTextFieldValueBuffer] = useState(value);

  const handleChangeDebounced = useCallback(debounce(onChange, 800), [onChange]);

  const findEnumSymbols = (): string[] => {
    if (field?.allowedValues?.[0]) {
      if (!['string', 'number'].includes(typeof field.allowedValues[0])) {
        return (field.allowedValues as SmartQueryEnumItem[]).map(({ symbol }) => symbol);
      }
      return field.allowedValues.map((val) => val.toString());
    }
    return [];
  };

  const findEnumLabel = (symbol: string | boolean | string[]): string => {
    // Handle array case (shouldn't happen but TypeScript needs it)
    if (Array.isArray(symbol)) {
      return symbol[0] || '';
    }

    // Convert boolean to string for lookup
    const symbolStr = typeof symbol === 'boolean' ? symbol.toString() : symbol;

    if (field?.allowedValues?.[0] && typeof field.allowedValues[0] === 'object' && 'name' in field.allowedValues[0]) {
      const enumItem = (field.allowedValues as SmartQueryEnumItem[]).find((item) => item.symbol === symbolStr);
      return enumItem ? localize(enumItem.name) : symbolStr;
    }
    return symbolStr;
  };

  // Render ENUM field
  const renderEnumEditor = (): JSX.Element => {
    // Convert boolean values to string for ENUM fields (e.g., isPremium: true -> "true")
    const enumValue = typeof value === 'boolean' ? value.toString() : value;
    const displayValue =
      operator === SmartQueryOperatorSymbol.ANY_OF ? (flatten([enumValue]) as string[]) : (enumValue as string);

    return (
      <Autocomplete
        className={cx(className, classes.fieldContainer)}
        value={displayValue}
        options={findEnumSymbols()}
        multiple={operator === SmartQueryOperatorSymbol.ANY_OF}
        getOptionLabel={(option) => findEnumLabel(option)}
        onChange={(_, newValue) => {
          // For boolean ENUMs, convert string back to boolean
          if (
            field?.allowedValues &&
            field.allowedValues.length === 2 &&
            typeof field.allowedValues[0] === 'object' &&
            'symbol' in field.allowedValues[0] &&
            (field.allowedValues[0].symbol === 'true' || field.allowedValues[0].symbol === 'false')
          ) {
            const boolValue = newValue === 'true';
            handleChangeDebounced(boolValue);
          } else {
            handleChangeDebounced(newValue as string | number | string[] | number[] | undefined);
          }
        }}
        renderInput={(params) => (
          <MuiTextField
            {...params}
            label={localize(field.name)}
            color={formControlColor}
            className={classes.textField}
            variant={variant}
            inputProps={{
              ...params.inputProps,
              role: 'combobox'
            }}
          />
        )}
        clearOnEscape
        disableClearable
        autoComplete
        color={formControlColor}
        disabled={disabled}
      />
    );
  };

  // Render DATETIME field
  const renderDateTimeEditor = (): JSX.Element => {
    // BETWEEN is a frontend-only operator injected here since the backend does not return it.
    const dateOperators = [
      ...(field.allowedOperators?.filter((op) =>
        [SmartQueryOperatorSymbol.EQ, SmartQueryOperatorSymbol.LT, SmartQueryOperatorSymbol.GT].includes(op.symbol)
      ) || []),
      { symbol: BETWEEN_OPERATOR_SYMBOL as SmartQueryOperatorSymbol, name: { en: 'Between', es: 'Entre' } }
    ];

    const showOperatorSelect = onOperatorChange && dateOperators.length > 0;
    const isBetween = (operator as string) === BETWEEN_OPERATOR_SYMBOL;
    const dateArray = isBetween && Array.isArray(value) ? value : [value, value];
    const startDate = dateArray[0] as string;
    const endDate = dateArray[1] as string;

    return (
      <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {showOperatorSelect && (
          <MuiFormControl variant={variant} className={classes.fieldContainer} style={{ minWidth: 120 }}>
            <InputLabel color={formControlColor}>{t('general.operator')}</InputLabel>
            <Select
              value={operator}
              color={formControlColor}
              onChange={(e) => {
                const newOp = e.target.value as SmartQueryOperatorSymbol;
                onOperatorChange(newOp);
                if ((newOp as string) === BETWEEN_OPERATOR_SYMBOL && !Array.isArray(value)) {
                  const currentDate = (value as string) || DateTime.now().toISODate();
                  onChange([currentDate, currentDate]);
                } else if ((newOp as string) !== BETWEEN_OPERATOR_SYMBOL && Array.isArray(value)) {
                  onChange(value[0]);
                }
              }}
              label={t('general.operator')}
              disabled={disabled}
            >
              {dateOperators.map((op) => (
                <MenuItem key={op.symbol} value={op.symbol}>
                  {op.symbol === SmartQueryOperatorSymbol.EQ
                    ? t('query_builder.operators.exactly')
                    : op.symbol === SmartQueryOperatorSymbol.LT
                      ? t('query_builder.operators.before')
                      : op.symbol === SmartQueryOperatorSymbol.GT
                        ? t('query_builder.operators.after')
                        : (op.symbol as string) === BETWEEN_OPERATOR_SYMBOL
                          ? t('query_builder.operators.between')
                          : localize(op.name)}
                </MenuItem>
              ))}
            </Select>
          </MuiFormControl>
        )}

        <DatePicker
          label={isBetween ? t('date_picker.from') : localize(field.name)}
          value={startDate ? DateTime.fromISO(startDate) : null}
          onChange={(newValue) => {
            const isoDate = newValue?.toISODate();
            if (isBetween) {
              onChange([isoDate || '', endDate]);
            } else {
              onChange(isoDate || undefined);
            }
          }}
          slotProps={{
            textField: {
              color: formControlColor,
              variant: variant,
              className: cx(className, classes.fieldContainer, classes.textField)
            },
            actionBar: {
              actions: ['clear', 'today']
            }
          }}
          disabled={disabled}
        />

        {isBetween && (
          <DatePicker
            label={t('date_picker.to')}
            value={endDate ? DateTime.fromISO(endDate) : null}
            onChange={(newValue) => {
              const isoDate = newValue?.toISODate();
              onChange([startDate, isoDate || '']);
            }}
            slotProps={{
              textField: {
                color: formControlColor,
                variant: variant,
                className: cx(className, classes.fieldContainer, classes.textField)
              },
              actionBar: {
                actions: ['clear', 'today']
              }
            }}
            disabled={disabled}
            minDate={startDate ? DateTime.fromISO(startDate) : undefined}
          />
        )}
      </div>
    );
  };

  // Render STRING/NUMBER fields (default)
  const renderDefaultEditor = (): JSX.Element => {
    const isNumericInput = field?.type === 'NUMBER';
    const numericInputProps = (
      isNumericInput
        ? {
            type: 'number',
            inputMode: 'numeric',
            pattern: '[0-9]*',
            ...(field?.symbol === SmartQueryFieldSymbol.YEAR_RELEASED
              ? { min: 1890, max: new Date().getFullYear() + 5 }
              : {})
          }
        : {}
    ) as InputBaseComponentProps;

    // If field has suggestions, use SuggestionsAutocomplete
    if (field?.allowSuggestions) {
      return (
        <SuggestionsAutocomplete
          classNames={{ autocomplete: cx(className, classes.fieldContainer), textField: classes.textField }}
          propertyName={field.allowSuggestions.propertyName}
          value={textFieldValueBuffer as string}
          onChange={(newValue) => {
            handleChangeDebounced(newValue);
            setTextFieldValueBuffer(newValue);
          }}
          label={localize(field.name)}
          disabled={disabled}
        />
      );
    }

    // If operator is any-of, render multi-value autocomplete
    if (operator === SmartQueryOperatorSymbol.ANY_OF) {
      const currentValue = typeof value === 'string' ? [value] : (value as string[]);
      return (
        <Autocomplete
          multiple
          freeSolo
          className={cx(className, classes.fieldContainer)}
          options={[]}
          value={currentValue || []}
          onChange={(_, newValue) => handleChangeDebounced(newValue)}
          renderTags={(tagValue: readonly string[], getTagProps) =>
            tagValue.map((option: string, index: number) => (
              <Chip {...getTagProps({ index })} key={`${index}-${option}`} label={option} />
            ))
          }
          renderInput={(params) => (
            <MuiTextField
              {...params}
              label={localize(field.name)}
              color={formControlColor}
              className={classes.textField}
              variant={variant}
              inputProps={{ ...params.inputProps, role: 'combobox' }}
            />
          )}
          clearOnEscape
          disableClearable
          color={formControlColor}
          disabled={disabled}
        />
      );
    }

    // Default text/number input
    return (
      <MuiTextField
        label={localize(field.name)}
        value={textFieldValueBuffer?.toString() || ''}
        className={cx(classes.textField, className, classes.fieldContainer)}
        color={formControlColor}
        variant={variant}
        inputProps={numericInputProps}
        onChange={(event) => {
          const newValue = event.target.value?.trimStart();
          const parsedNewValue = isNumericInput ? Number(newValue) : newValue;
          setTextFieldValueBuffer(parsedNewValue);
          handleChangeDebounced(parsedNewValue);
        }}
        disabled={disabled}
      />
    );
  };

  // Main render logic based on field type
  switch (field?.type) {
    case 'ENUM':
      return renderEnumEditor();
    case 'DATETIME':
      return renderDateTimeEditor();
    case 'NUMBER':
    case 'STRING':
    default:
      return renderDefaultEditor();
  }
}

export default SmartQueryFieldRenderer;
